import React, { useState } from 'react';

import { Search, X } from 'lucide-react';

interface SearchBarProps {
  value: string;
  onChange: (value: string) => void;
  onSearch?: (value: string) => void;
  placeholder?: string;
  className?: string;
}

export const SearchBar: React.FC<SearchBarProps> = ({
  value,
  onChange,
  onSearch,
  placeholder = 'Search...',
  className = '',
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const handleClose = () => {
    setIsExpanded(false);
    onChange('');
    onSearch?.('');
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    onSearch?.(value);
  };

  if (!isExpanded) {
    return (
      <button
        type="button"
        onClick={() => setIsExpanded(true)}
        className={`
          w-10 h-10 
          flex items-center justify-center 
          text-neutral-600 dark:text-neutral-400 
          bg-white dark:bg-neutral-950 
          border border-neutral-200 dark:border-neutral-800 
          hover:border-brand-300 dark:hover:border-brand-700 
          rounded-lg 
          transition-all
          ${className}
        `
          .trim()
          .replace(/\s+/g, ' ')}
        aria-label="Search"
      >
        <Search className="w-5 h-5" />
      </button>
    );
  }

  return (
    <div className={`relative ${className}`} data-flyout-container>
      <form
        onSubmit={handleSubmit}
        className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-neutral-950 border border-neutral-200 dark:border-neutral-800 rounded-lg shadow-sm w-full sm:max-w-[360px] min-w-0"
      >
        <button
          type="submit"
          className="text-neutral-400 dark:text-neutral-600"
          aria-label="Submit search"
        >
          <Search className="w-4 h-4" />
        </button>
        <input
          type="text"
          placeholder={placeholder}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          className="flex-1 min-w-0 bg-transparent text-sm text-neutral-900 dark:text-white placeholder:text-neutral-400 dark:placeholder:text-neutral-600 focus:outline-none"
          autoFocus
        />
        {value && (
          <button
            type="button"
            onClick={() => {
              onChange('');
              onSearch?.('');
            }}
            className="w-5 h-5 flex items-center justify-center rounded-full bg-brand-200 dark:bg-brand-700 text-neutral-500 dark:text-neutral-300 hover:bg-brand-300 dark:hover:bg-brand-600 transition-colors mr-1"
            title="Clear Search"
          >
            <X className="w-3 h-3" />
          </button>
        )}
        <button
          type="button"
          onClick={handleClose}
          className="text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-white transition-colors"
          title="Close Search"
        >
          <X className="w-4 h-4" />
        </button>
      </form>
    </div>
  );
};
